
import type {GroupReservationModel} from "@/serviceWorker/workerComponents/Models/GroupReservationModel";
import {ShopChosenModel} from "@/serviceWorker/workerComponents/Models/ShopChosenModel";
import type {ReservationSearchData} from "@/serviceWorker/workerComponents/Models/ReservationSearchData";
import type {RoomStandardDescriptionModel} from "@/serviceWorker/workerComponents/Models/RoomStandardDescriptionModel";
import type {OfferAvailability} from "@/serviceWorker/workerComponents/Models/OfferAvailability";
import type {CalculationModel} from "@/serviceWorker/workerComponents/Models/CalculationModel";
import type {OfferModel} from "@/serviceWorker/workerComponents/Models/OfferModel";
import type {ChosenReservationModel} from "@/serviceWorker/workerComponents/Models/ChosenReservationModel";
import type {StandardAvailabilityData} from "@/frontendClasses/StandardAvailabilityData";
import type {NfServiceWorkerCommunication} from "@/services/NfServiceWorkerCommunication";
import {NoAvailableRoomsException} from "@/exceptions/NoAvailableRoomsException";
import {SetGroupReservationBasket} from "@/serviceWorker/workerComponents/MessageTypes/SetGroupReservationBasket";
import type {AvailabilityData} from "@/serviceWorker/workerComponents/MessageTypes/AvailabilityData";
import {RoomPeopleConfiguration} from "@/serviceWorker/workerComponents/Models/RoomPeopleConfiguration";
import {NfPricesService} from "@/services/NfPricesService";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";
import {MaxAmountOfReservationsInGroupException} from "@/exceptions/MaxAmountOfReservationsInGroupException";
import {GroupReservationNotAllowedException} from "@/exceptions/GroupReservationNotAllowedException";
import type { GroupReservationMode } from "@/frontendClasses/GroupReservationMode";
import {GetReservationCalculation} from "@/serviceWorker/workerComponents/MessageTypes/GetReservationCalculation";
import {NfHotelShopStore} from "@/store/NfHotelShopStore";
import {get, has, set} from "lodash-es";
import {NfBasketStore} from "@/store/NfBasketStore";
import type {AnalyticsService} from "@/services/NfAnalyticsService";
import type {Ref} from "vue";



/**
 * Serwis do obsługi rezerwacji grupowej
 * Dostępny w komponentach poprzez inject: {groupReservationBasket: {from: 'groupReservationBasket'}}
 */
export class NfGroupReservationService {
    constructor(protected groupReservationBasket: GroupReservationModel| null,
                protected chosenShop: ShopChosenModel| null,
                protected reservationSearchData: ReservationSearchData| null,
                protected standardChosen: RoomStandardDescriptionModel| null,
                protected bookingChosen: OfferAvailability| null,
                protected offerChosen: OfferModel| null,
                protected calculation: CalculationModel| null ,
                protected config: Ref<BookingEngineConfigModel> | null,
                protected analyticsService: AnalyticsService,
                protected communicationService: NfServiceWorkerCommunication) {
    }

    private getConfig(): BookingEngineConfigModel|null {
        if (!this.config) {
            return null;
        }
        if (this.config.value) {
            return this.config.value;
        }
        //@ts-ignore
        return this.config;
    }
    public onReservationSearchDataChanged(reservationSearchData: ReservationSearchData) {
        if((this.getGroupReservationMode(reservationSearchData) == 'multi-room-choice'
            ||this.getGroupReservationMode() == 'multi-room-choice' )
            && this.groupReservationBasket?.reservations) {
            const reservationsToRemove = [];
            for(const reservation of this.groupReservationBasket?.reservations) {
                if(reservation.chosenPeopleConfiguration.uniqueId == reservationSearchData.uniqueId) {
                    continue;
                }
                if(reservationSearchData.extraRooms?.length) {
                    if(reservationSearchData.extraRooms.some(r => r.uniqueId == reservation.chosenPeopleConfiguration.uniqueId)) {
                        continue;
                    }
                }
                reservationsToRemove.push(reservation)
            }
            for(const reservation of reservationsToRemove) {
                this.removeFromBasket(reservation);
            }
        }
    }

    /**
     * powierzchowne sprawdzenie czy możemy dodawać rezerwacje.
     * służy on TYLKO do pokazania informacji w koszyku labelki że nie można tworzyć rez. grup.
     * do większych i poprawnych walidacji używać canAddToBasket.
     */
    public canAddMoreReservationsToBasket(): boolean {
        return this.canAddReservation();
    }

    /**
     * Metoda sprawdzająca, czy można dodać rezerwację na podstawie bardziej szczegółowych kryteriów.
     */
    public canAddToBasket(roomStandardId: number, dateFrom: moment.Moment, dateTo: moment.Moment): boolean {
        if (!this.canAddReservation()) {
            return false;
        }

        let availableRoomsInStandard: number | null = null;
        let takenRoomsInStandard = 0;
        const matchingReservations = this.getMatchingReservations(dateTo, dateFrom, roomStandardId);

        for (const reservation of matchingReservations) {
            if (matchingReservations.length === reservation.chosenPrices.available) {
                return false;
            }
            if (availableRoomsInStandard === null) {
                availableRoomsInStandard = reservation.chosenPrices.available;
            } else {
                availableRoomsInStandard = Math.min(availableRoomsInStandard, reservation.chosenPrices.available);
            }
            takenRoomsInStandard++;
        }

        if (availableRoomsInStandard === null) {
            // Gdy nie ma jeszcze żadnej rezerwacji w przecinającym się terminie i standardzie
            return true;
        }
        return availableRoomsInStandard > 0;
    }

    /**
     * Sprawdza, czy można dodać rezerwację do koszyka na podstawie ogólnych zasad.
     */
    private canAddReservation(): boolean {
        if (!this.groupReservationBasket) {
            return true;
        }

        // Sprawdzenie, czy dozwolone są rezerwacje grupowe
        if (!this.isGroupReservationAllowed() && this.groupReservationBasket.reservations.length > 0) {
            return false;
        }

        // Sprawdzenie maksymalnej liczby pokoi w koszyku
        return !this.isMaxRoomCountReached();
    }

    /**
     * Sprawdza, czy rezerwacje grupowe są dozwolone.
     */
    private isGroupReservationAllowed(): boolean {
        return this.getConfig()?.allow_multiple_reservation_rooms === '1';
    }

    /**
     * Sprawdza, czy maksymalna liczba pokoi w koszyku została osiągnięta.
     */
    private isMaxRoomCountReached(): boolean {
        const maxRooms = this.getConfig()?.max_amount_of_rooms_in_group || 1;
        if (!this.groupReservationBasket) {
            return true;
        }
        return this.groupReservationBasket.reservations.length >= maxRooms;
    }


    public getGroupReservationMode(reservationSearchData?: ReservationSearchData|null): GroupReservationMode {
        if(!reservationSearchData) {
            reservationSearchData = this.reservationSearchData;
        }
        if((reservationSearchData?.extraRooms?.length || 0) >= 1) {
            return 'multi-room-choice';
        } else if((this.groupReservationBasket?.reservations?.length || 0) > 1) {
            return 'one-by-one-choice';
        } else {
            return 'single-reservation';
        }
    }

    private getGroupReservationBasket = (): GroupReservationModel => {
        let groupReservationBasket = this.groupReservationBasket;

        if (get(groupReservationBasket, 'value.reservations', false)) {
            return groupReservationBasket as GroupReservationModel;
        }
        
        groupReservationBasket = Object.assign({}, NfGroupReservationService.emptyGroupReservationModel);
        groupReservationBasket.reservations = [];
     
        return groupReservationBasket;
    }

    public onLoadedBasket(): void {
        const reservations: ChosenReservationModel[] | null = NfBasketStore.loadLocalBasket();

        if (reservations) {
            const group = this.getGroupReservationBasket();
            reservations.forEach(reservation => {
                const res = this.configureReservation(reservation, group, true);
                if (!group.reservations) {
                    group.reservations = [];
                }
                group.reservations.push(res);
            })
            this.recalculateBasket(group);
            this.sendBasket(group);
        }
    }


    /**
     * Szuka rezerwacji w koszyku
     * @param dateTo
     * @param dateFrom
     * @param roomStandardId
     */
    public getMatchingReservations(dateTo: moment.Moment, dateFrom: moment.Moment, roomStandardId: number|null) {
        const result:ChosenReservationModel[] = [];
        if(!this.groupReservationBasket) {
            return result;
        }
        for (const reservation of this.groupReservationBasket.reservations) {
            if ( (roomStandardId === null || reservation.chosenStandardDescription.roomstandard_id == roomStandardId)
                && reservation.reservationSearchData.dateFrom?.isBefore(dateTo, 'day')
                && reservation.reservationSearchData.dateTo?.isAfter(dateFrom, 'day')) {
                result.push(reservation);
            }
        }
        return result;
    }

    /**
     * Przetwarza dane z serwera dotyczące dostępności pokoi
     * Dodaje dodatkowe pola do danych o możliwych do wybrania jeszcze pokojach z grupy wyszukiwanych pokojów
     * @param availabilityData
     * @param standardListsToUpdate lista list standardów w których należy aktualizować widoczne oferty
     * @returns Zwraca listę zmienionych ofert per id standardu
     */
    public processAvailabilityResult(availabilityData: AvailabilityData, ...standardListsToUpdate: (StandardAvailabilityData[]|null)[]): {[roomStandardId:number]: OfferAvailability[]} {
        if(!this.reservationSearchData?.extraRooms?.length) {
            //Jeśli nie ma wyszukiwania dodatkowych konfiguracji, to nie mamy czego przetwarzać
            return {};
        }
        const changedStandardOffers:{[roomStandardId:number]: OfferAvailability[]} = {};
        const fromDate = self.moment(availabilityData.availabilityRequest.fromDate);
        const toDate = self.moment(availabilityData.availabilityRequest.toDate);
        const reservationsByRoomStandardId: Record<string,ChosenReservationModel[]> = {};
        /**
         * Tutaj zbieramy numery pokojów, które czekają na wybór standardu
         */
        const unchosenRoomNumbersPerConfigurationIds: Record<string,Record<number,true>> = {};
        if(this.reservationSearchData) {
            unchosenRoomNumbersPerConfigurationIds[this.reservationSearchData.getConfigurationId()] = {[this.reservationSearchData.uniqueId]: true};
            if(this.reservationSearchData.extraRooms) {
                for(const extraRoom of this.reservationSearchData.extraRooms) {
                    set(
                        unchosenRoomNumbersPerConfigurationIds,
                        [extraRoom.getConfigurationId(), extraRoom.uniqueId.toString()],
                        get(
                            unchosenRoomNumbersPerConfigurationIds,
                            [extraRoom.getConfigurationId(), extraRoom.uniqueId],
                            0
                        ) as number + 1
                    );
                }
            }
        }
        for(const reservation of this.getMatchingReservations(toDate,fromDate,null)) {
            const configurationId = reservation.chosenPeopleConfiguration.getConfigurationId();
            if(has(unchosenRoomNumbersPerConfigurationIds, [configurationId, reservation.chosenPeopleConfiguration.uniqueId])) {
                const roomIdsInConfiguration =  unchosenRoomNumbersPerConfigurationIds[configurationId];
                delete roomIdsInConfiguration[reservation.chosenPeopleConfiguration.uniqueId];
                if(!Object.keys(roomIdsInConfiguration).length) {
                    delete unchosenRoomNumbersPerConfigurationIds[configurationId];
                }
            }
            const reservations = get(
                reservationsByRoomStandardId,
                [
                    reservation.chosenStandardDescription.roomstandard_id,
                    reservation.chosenPeopleConfiguration.getConfigurationId()
                ],
                []
            );
            reservations.push(reservation);
            set(
                reservationsByRoomStandardId,
                [
                    reservation.chosenStandardDescription.roomstandard_id,
                    reservation.chosenPeopleConfiguration.getConfigurationId()
                ],
                reservations
            );
        }
        for(const availableStandard of availabilityData.availabilityData.availability) {
            const availableConfigurations = availableStandard.configurations!;
            let cheapestConfigurationAvailableForChoosing: typeof availableConfigurations['']|null = null;
            let cheapestConfigurationAlreadyChosen: typeof availableConfigurations['']|null = null;
            availableStandard.availableForRoomUniqueIds = [];
            availableStandard.roomUniqueIdsTaken = [];
            if(availableConfigurations) {
                for(const configurationId in availableConfigurations) {
                    const configurationWaitsForChoosing = configurationId in unchosenRoomNumbersPerConfigurationIds;
                    const availableOffersForConfiguration = availableConfigurations[configurationId];
                    const reservationsInBasket: ChosenReservationModel[] = get(reservationsByRoomStandardId, [availableStandard.id, configurationId],[]);
                    for(const reservation of reservationsInBasket) {
                        availableStandard.roomUniqueIdsTaken.push(reservation.chosenPeopleConfiguration.uniqueId);
                    }
                    const availableRooms = availableOffersForConfiguration.offer[0].available - reservationsInBasket.length;
                    if(!configurationWaitsForChoosing) {
                        if(cheapestConfigurationAlreadyChosen === null) {
                            cheapestConfigurationAlreadyChosen = availableOffersForConfiguration;
                        } else {
                            if(availableOffersForConfiguration.offer[0].displayed_price < cheapestConfigurationAlreadyChosen.offer[0].displayed_price) {
                                cheapestConfigurationAlreadyChosen = availableOffersForConfiguration;
                            }
                        }
                    }
                    if(configurationWaitsForChoosing && availableRooms > 0) {
                        availableStandard.availableForRoomUniqueIds.push(...Object.keys(unchosenRoomNumbersPerConfigurationIds[configurationId]).map(parseInt));
                        if(cheapestConfigurationAvailableForChoosing === null) {
                            cheapestConfigurationAvailableForChoosing = availableOffersForConfiguration;
                        } else {
                            if(availableOffersForConfiguration.offer[0].displayed_price < cheapestConfigurationAvailableForChoosing.offer[0].displayed_price) {
                                cheapestConfigurationAvailableForChoosing = availableOffersForConfiguration;
                            }
                        }
                        continue;
                    }
                    if(availableRooms < 0) {
                        console.error(`Wybrano więcej pokoi niż jest dostępnych w standardzie ${availableStandard.id}, okresie ${fromDate.format('YYYY-MM-DD')}-${toDate.format('YYYY-MM-DD')}`);
                        //STODO: wybrano więcej niż jest dostępnych, zapisać listę do usunięcia z koszyka
                    }
                }
                if(cheapestConfigurationAvailableForChoosing !== null) {
                    availableStandard.offer = cheapestConfigurationAvailableForChoosing.offer;
                } else {
                    availableStandard.offer = cheapestConfigurationAlreadyChosen?.offer ?? [];
                }
                changedStandardOffers[availableStandard.id] = availableStandard.offer;
                //STODO: jeśli jakaś rezerwacja powinna tu być, ale już nie ma na nią miejsca, to usunąć ją z koszyka
            } else {
                //Nie jesteśmy w trybie wyszukiwania grupówki, więc nie musimy ustawiać availableStandard.offer ani availableStandard.availableForRoomUniqueIds
            }
        }
        //region aktualizujemy oferty w podanych listach
        for(const standardList of standardListsToUpdate) {
            if(standardList) {
                for (const standard of standardList) {
                    if (standard.availability && (standard.description.roomstandard_id in changedStandardOffers)) {
                        standard.availability.offer = changedStandardOffers[standard.description.roomstandard_id];
                    }
                }
            }
        }
        //endregion
        return changedStandardOffers;
    }
    /**
     * Tworzy dane rezerwacji do koszyka
     * Zakłada, że dane aktualnych wyborów są przechowywane w komponencie
     * @param chosenOffer jeśli nie jest podane, to wybierze aktualnie wybraną ofertę (jeśli na użytkownik wybrał jakąś na wcześniejszym etapie) - jeśli nie, to rzuci wyjątek
     */
    public createChosenReservationModel(standardData: StandardAvailabilityData, chosenOffer?: OfferModel, peopleConfiguration?: RoomPeopleConfiguration): ChosenReservationModel {
        let _chosenOffer: OfferModel;
        if (typeof (chosenOffer) === 'undefined') {
            if (this.offerChosen) {
                _chosenOffer = this.offerChosen;
            } else {
                throw new Error('No offer chosen');
            }
        } else {
            _chosenOffer = chosenOffer;
        }

        const chosenPrices = standardData.availability?.offer.find(o => o.id == _chosenOffer.id);
        if (!chosenPrices) {
            throw new Error('Chosen offer not found in standard availability');
        }
        const chosenRoomId = this.findAvailableRoom(standardData.description.roomstandard_id, chosenPrices);

        return {
            chosenStandardDescription: standardData.description,
            chosenPrices,
            chosenOffer: _chosenOffer,
            reservationSearchData: this.reservationSearchData,
            chosenRoomId,
            chosenPeopleConfiguration: peopleConfiguration ?? this.reservationSearchData?.getPeopleConfiguration(),
            reservationUniqueId: NfGroupReservationService.nextReservationUniqueId++,
            calculationRequested: false
        } as ChosenReservationModel;

    }

    /**
     * Szuka pokoju do kolejnej rezerwacji grupowej
     * Sprawdza wśród zarezerwowanych wcześniej rezerwacji grupowych czy nie ma już wybranego pokoju w tym standardzie
     * @param standardData
     * @param availability
     * @private
     */
    public findAvailableRoom(roomStandardId: number, availability: OfferAvailability) {
        let chosenRoomId: string | null = null;
        if (this.groupReservationBasket) {
            const alreadyChosenRooms: Record<string, boolean> = {};
            for (const reservation of this.groupReservationBasket.reservations) {
                if (reservation.chosenStandardDescription.roomstandard_id == roomStandardId
                && reservation.reservationSearchData.dateFrom?.isAfter(this.reservationSearchData?.dateFrom, 'day')
                && reservation.reservationSearchData.dateTo?.isBefore(this.reservationSearchData?.dateFrom, 'day')) {
                    alreadyChosenRooms[reservation.chosenRoomId] = true;
                }
            }
            for (const roomId of availability.rooms) {
                if (!alreadyChosenRooms[roomId]) {
                    chosenRoomId = roomId;
                    break;
                }
            }
            if (!chosenRoomId) {
                throw new NoAvailableRoomsException();
            }
            return chosenRoomId;
        } else {
            if (!availability.rooms.length) {
                throw new NoAvailableRoomsException();
            }
            return availability.rooms[0];
        }
    }
    requestCalculation(basket: GroupReservationModel|null = null) {
        if (basket === null) {
            basket = this.groupReservationBasket;
        }
        let requestedSome = false;
        if(basket && this.communicationService) {
            for(const reservation of basket.reservations) {
                if(!reservation.calculationRequested) {
                    if(!reservation.chosenShop) {
                        reservation.chosenShop = new ShopChosenModel();
                    }
                    this.communicationService.sendMessage(new GetReservationCalculation(reservation.reservationSearchData, reservation.chosenShop,
                        reservation.chosenOffer.id, reservation.chosenStandardDescription.roomstandard_id,
                        reservation.chosenPrices.applied_discount, reservation.reservationUniqueId));
                    reservation.calculationRequested = requestedSome = true;
                    this.setReservation(reservation, basket);
                }
            }
        }
        return requestedSome;
    }

    clearCalculationRequested() {
        for (const reservation of this.groupReservationBasket!.reservations) {
            reservation.calculationRequested = false;
        }
    }
    private recalculateBasket(basket: GroupReservationModel): void {
        basket.total_adults = 0;
        basket.total_children = 0;
        basket.shop_price = 0;
        basket.stay_crossed_price = 0;
        basket.cleaning_crossed_price = 0;
        basket.city_tax_crossed_price = 0;
        basket.stay_displayed_price = 0;
        basket.cleaning_displayed_price = 0;
        basket.city_tax_displayed_price = 0;
        basket.displayed_price = 0;
        basket.crossed_price = 0;
        const pricesService = new NfPricesService();
        for(const reservation of basket.reservations) {
            const mainPrices = reservation.chosenPrices;
            const applicablePrices = pricesService.getApplicablePrices(reservation.chosenPrices);
            basket.city_tax_crossed_price += mainPrices.city_tax_price || 0;
            basket.cleaning_crossed_price += mainPrices.cleaning_price || 0;

            basket.stay_crossed_price += mainPrices.standard_price + pricesService.getBookingPricesSum(mainPrices);

            basket.total_adults += reservation.chosenPeopleConfiguration?.adultCount || 0;
            basket.total_children += reservation.chosenPeopleConfiguration?.childrenAges?.length || 0;
            if(!reservation.calculation) { // Kalkulacja ma priorytet nad cenami z oferty
                // @ts-ignore Wrzucamy tymczasowo zastępcze dane do kalkulacji rezerwacji, żeby było co wyświetlać
                reservation.calculation = {
                    // @ts-ignore Te dane będą później zastąpione i uzupełnione gdy przyjdzie kalkulacja z backendu
                    prices: {
                        additions_price: reservation.chosenShop?.sum || 0,
                        city_tax_price: mainPrices.displayed_city_tax_price || 0,
                        code_discount_city_tax: 0,
                        stay_price: mainPrices.displayed_price,
                        code_discount: 0,
                        cleaning: mainPrices.displayed_cleaning_price || 0,
                        code_discount_cleaning: 0
                    }
                };
            }

            basket.shop_price += reservation.chosenShop?.sum || 0;
            basket.shop_price = parseFloat(basket.shop_price.toFixed(2));
            basket.stay_displayed_price += (reservation.calculation.prices.stay_price || 0) - (reservation.calculation.prices.code_discount || 0);
            basket.cleaning_displayed_price += (reservation.calculation.prices.cleaning || 0) - (reservation.calculation.prices.code_discount_cleaning || 0);
            basket.city_tax_displayed_price += (reservation.calculation.prices.city_tax_price || 0) - (reservation.calculation.prices.code_discount_city_tax || 0);
            basket.crossed_price += reservation.calculation.prices.crossed_stay_price + (reservation.calculation.prices.cleaning || 0) + (reservation.calculation.prices.city_tax_price || 0);
        }
        basket.total_price = basket.shop_price + basket.displayed_price;
        basket.crossed_price = basket.shop_price + basket.crossed_price;
        basket.displayed_price = basket.city_tax_displayed_price + basket.cleaning_displayed_price + basket.stay_displayed_price + basket.shop_price;
        if(basket.city_tax_crossed_price <= basket.city_tax_displayed_price) {
            basket.city_tax_crossed_price = null;
        }
        if(basket.cleaning_crossed_price <= basket.cleaning_displayed_price) {
            basket.cleaning_crossed_price = null;
        }
        if(basket.stay_crossed_price <= basket.stay_displayed_price) {
            basket.stay_crossed_price = null;
        }
        if(basket.crossed_price <= basket.displayed_price) {
            basket.crossed_price = null;
        }

        this.requestCalculation(basket);
    }

    private sendBasket(basket: GroupReservationModel){
        this.communicationService.sendMessage(new SetGroupReservationBasket(null, basket));
    }

    static nextReservationUniqueId = 1;
    static readonly emptyGroupReservationModel: GroupReservationModel = {
        city_tax_displayed_price: 0,
        cleaning_displayed_price: 0,
        stay_displayed_price: 0,
        reservations: [],
        cleaning_crossed_price: 0,
        city_tax_crossed_price: 0,
        stay_crossed_price: 0,
        shop_price: 0,
        total_price: 0,
        total_adults: 0,
        total_children: 0,
        displayed_price: 0,
        crossed_price: 0
    }

    /**
     * Czy jesteśmy w rezerwacji grupowej
     */
    public isGroupReservation() {
        return !!this.reservationSearchData?.extraRooms?.length;
    }

    public configureReservation = (reservationModel: ChosenReservationModel,
                                          groupReservationBasket: GroupReservationModel,
                                          onLoadData = false) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        if(this.getConfig()?.allow_multiple_reservation_rooms != '1' && groupReservationBasket.reservations.length) {
            throw new GroupReservationNotAllowedException();
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        if(get(groupReservationBasket,'value.reservations',[]).length >= (this.getConfig().max_amount_of_rooms_in_group || 1)) {
            throw new MaxAmountOfReservationsInGroupException();
        }

        if(!reservationModel.chosenPeopleConfiguration && this.reservationSearchData) {
            reservationModel.chosenPeopleConfiguration = new RoomPeopleConfiguration();
            reservationModel.chosenPeopleConfiguration.uniqueId = this.reservationSearchData.uniqueId;
            reservationModel.chosenPeopleConfiguration.adultCount = this.reservationSearchData.adultCount;
            reservationModel.chosenPeopleConfiguration.childrenAges = this.reservationSearchData.childrenAges;
            if(this.reservationSearchData.extraRooms?.length) {
                console.error('Wyszukiwano dodatkowe pokoje dla rezerwacji grupowej, ale nie podano wyboru grupy osób dla której ma być przypisana rezerwacja');
            }
        }

        if (onLoadData) {
            reservationModel.reservationSearchData.dateFrom = window.moment(reservationModel.reservationSearchData.dateFrom);
            reservationModel.reservationSearchData.dateTo = window.moment(reservationModel.reservationSearchData.dateTo);
        }

        return reservationModel;
    }

    /**
     *
     * @param {ChosenReservationModel} reservationModel
     * @param {boolean} onLoadData - jeśli dane są ładowane z locala wymagane jest ich dodatkowe obrobienie
     * dodatkowo nie dodajemy tego ponownie do zapisania.
     */
    public addToBasket(reservationModel: ChosenReservationModel, onLoadData = false) {
        let groupReservationBasket = this.groupReservationBasket;
        if(!groupReservationBasket) {
            groupReservationBasket = Object.assign({}, NfGroupReservationService.emptyGroupReservationModel);
            groupReservationBasket.reservations = [];
        }
        if(this.getConfig()?.allow_multiple_reservation_rooms != '1' && groupReservationBasket.reservations.length) {
            throw new GroupReservationNotAllowedException();

        }
        if(groupReservationBasket.reservations.length >= (this.getConfig()?.max_amount_of_rooms_in_group || 1)) {
            throw new MaxAmountOfReservationsInGroupException();
        }
        if(!reservationModel.chosenPeopleConfiguration && this.reservationSearchData) {
            reservationModel.chosenPeopleConfiguration = new RoomPeopleConfiguration();
            reservationModel.chosenPeopleConfiguration.uniqueId = this.reservationSearchData.uniqueId;
            reservationModel.chosenPeopleConfiguration.adultCount = this.reservationSearchData.adultCount;
            reservationModel.chosenPeopleConfiguration.childrenAges = this.reservationSearchData.childrenAges;
            if(this.reservationSearchData.extraRooms?.length) {
                console.error('Wyszukiwano dodatkowe pokoje dla rezerwacji grupowej, ale nie podano wyboru grupy osób dla której ma być przypisana rezerwacja');
            }
        }

        if (onLoadData) {
            reservationModel.reservationSearchData.dateFrom = window.moment(reservationModel.reservationSearchData.dateFrom);
            reservationModel.reservationSearchData.dateTo = window.moment(reservationModel.reservationSearchData.dateTo);
        }

        groupReservationBasket.reservations.push(reservationModel);
        this.recalculateBasket(groupReservationBasket);
        this.sendBasket(groupReservationBasket);
        NfHotelShopStore.addReservation(reservationModel).then(() => {
            if (!onLoadData && groupReservationBasket) {
                NfBasketStore.saveLocalBasket(groupReservationBasket);
            }
        })
    }

    removeFromBasketByStandard(standard: StandardAvailabilityData, offer?: OfferModel) {
        if (standard.availability?.id) {
            const reservation = this.groupReservationBasket?.reservations.find(res =>
                (+res.chosenStandardDescription.roomstandard_id === standard.availability!.id) &&
                (!offer || (res.chosenOffer.id === offer.id))
            );

            if (reservation) {
                this.removeFromBasket(reservation);
            }
        }
    }

    removeFromBasket(reservation: ChosenReservationModel) {
        const groupReservationBasket = this.groupReservationBasket;

        if (!groupReservationBasket) {
            return;
        }

        const reservationIndex = groupReservationBasket.reservations.findIndex(r => r.reservationUniqueId == reservation.reservationUniqueId);

        if(reservationIndex !== undefined && reservationIndex !== null && reservationIndex >= 0) {
            groupReservationBasket.reservations.splice(reservationIndex,1);
            NfHotelShopStore.removeReservation(reservation);
            this.recalculateBasket(groupReservationBasket);
            this.sendBasket(groupReservationBasket);
            NfBasketStore.removeFromBasket(reservation);
            this.analyticsService.googleRemoveFromCart(reservation);
        }
        //STODO: jeśli nie zostało już żadnych rezerwacji i nie jesteśmy na stronie wyszukiwania, to idziemy na stronę wyszukiwania
        //STODO: zablokować możliwość wejscia na inne strony niż wyszukiwanie gdy koszyk jest pusty
    }

    setReservation(reservation: ChosenReservationModel, groupReservationBasket: GroupReservationModel|null = null) {
        if (groupReservationBasket === null) {
            groupReservationBasket = this.groupReservationBasket;
            if(!groupReservationBasket) {
                NfBasketStore.removeLocalBasket();
                return;
            }
        }
        const reservationIndex = groupReservationBasket.reservations.findIndex(r => r.reservationUniqueId == reservation.reservationUniqueId);
        if(reservationIndex !== undefined && reservationIndex !== null && reservationIndex >= 0) {
            groupReservationBasket.reservations[reservationIndex] = reservation;
            this.recalculateBasket(groupReservationBasket);
            this.sendBasket(groupReservationBasket);
        }
    }

}
